import CandlestickLoader from "@/components/common/CandlestickLoader";
import { config } from "@/config";
import { NUMERIC_PARAM_TO_FILTER } from "@/config/parameterDependencies";
import { FileUpload } from "@/components/common/FileUpload";
import {
  FeatureBuilder,
  type Feature,
} from "@/components/feature-builder/FeatureBuilder";
import { PageHeader } from "@/components/layout";
import { SystemPausedBanner } from "@/components/SystemPausedBanner";
import { ParallelSelector } from "@/components/parallel-selector";
import { ParameterConfigPanel } from "@/components/parameter-config/ParameterConfigPanel";
import {
  ImportLcSettingsButton,
  type LcSettingsImportResult,
} from "@/components/tv-bridge/ImportLcSettingsButton";
import { ProgressRail } from "@/components/progress-rail/ProgressRail";
import { SampleDataBadge } from "@/components/sample-data/SampleDataBadge";
import { SampleDataSelector } from "@/components/sample-data/SampleDataSelector";
import { StudyConfigSummary } from "@/components/study-config-summary";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useEnhancedToast } from "@/components/ui/enhanced-toast";
import {
  GlassCard,
  GlassCardContent,
  GlassCardDescription,
  GlassCardHeader,
  GlassCardTitle,
} from "@/components/ui/glass-card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  LowBarCountWarningDialog,
  hasUserDismissedLowBarWarning,
  isBarCountLow,
  getRecommendedMinBars,
} from "@/components/ui/low-bar-count-warning-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { inferTimeframe } from "@/utils/inferTimeframe";
import { normalizeTimeframe } from "@/utils/normalizeTimeframe";
import { TimeframeSelector } from "@/components/common/TimeframeSelector";
import { useAuth } from "@/contexts/AuthContext";
import { useApi } from "@/hooks/useApi";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import {
  useFeatureSchema,
  FALLBACK_SLOT_DEFAULTS,
} from "@/hooks/useFeatureSchema";
import { useUserLimits } from "@/hooks/useUserLimits";
import { parseDataFile, AmbiguousDateFormatError } from "@/services/api";
import type { DateFormat } from "@/services/api";
import { DateFormatDialog } from "@/components/ui/date-format-dialog";
import {
  loadSampleData,
  SAMPLE_DATASETS,
  type SampleDataset,
} from "@/services/sampleData";
import type { OptimizationRequest } from "@/types/api";
import type { DataPoint } from "@/types/database-adapter";
import { cn } from "@/utils/cn";
import {
  clampFeatureSlots,
  deriveSlotStates,
  slotStatesToTomlFeatures,
} from "@/utils/featureParams";
import type { FeatureParamState } from "@/utils/featureParams";
import { buildOptimizationRequest } from "@/utils/optimizationRequest";
import { logger } from "@/utils/logger";
import { assessOptimizationPayload } from "@/utils/payloadSize";
import { generateTomlConfig } from "@/utils/tomlGenerator";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  Activity,
  CheckCircle,
  Code,
  FileText,
  Info,
  Loader2,
  Rocket,
  Settings,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const NEW_STUDY_AUTH_MESSAGES = [
  "Verifying strategist credentials",
  "Spinning up optimization workspace",
  "Priming sample datasets",
  "Opening the study configurator",
];

export function NewStudyModern() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { addToast } = useEnhancedToast();
  const { aiewToken, isLoading: authLoading, login } = useAuth();
  const api = useApi(); // Use the authenticated API instance
  const lcBridge = useFeatureFlag("optimizer.lc_bridge");
  // AIE-531: per-feature-type catalog (arity/labels/bounds) + canonical slot defaults.
  const { featureTypes, slotDefaults } = useFeatureSchema();
  // AIE-531 Phase 6: this flag gates the feature-param RANGE controls only — the routing/edit path
  // is unconditional. 404 -> false (fail-safe), so range controls stay hidden until the flag exists.
  const { value: featureRangeEnabled } = useFeatureFlag(
    "optimizer.feature_param_optimization",
  );
  const { data: userLimits } = useUserLimits();
  const [activeSection, setActiveSection] = useState("data");
  const [file, setFile] = useState<File | null>(null);
  const [data, setData] = useState<DataPoint[] | null>(null);
  const [studyName, setStudyName] = useState("");
  const [nTrials, setNTrials] = useState(100);
  const [nTrialsInput, setNTrialsInput] = useState("100");
  const [symbol, setSymbol] = useState("BTC_USD");
  const [timeframe, setTimeframe] = useState<string | null>(null);
  const [_sourceFilename, setSourceFilename] = useState<string | null>(null);
  const [, setNormalizedJson] = useState<string | null>(null);
  const [fileValidationError, setFileValidationError] = useState<
    string | undefined
  >(undefined);
  const [isValidatingFile, setIsValidatingFile] = useState(false);
  // Sample data UX
  const [usingSampleData, setUsingSampleData] = useState(false);
  const [sampleDataset, setSampleDataset] = useState<SampleDataset | null>(
    null,
  );
  const [showSampleDataDialog, setShowSampleDataDialog] = useState(false);
  const [isLoadingSampleData, setIsLoadingSampleData] = useState(false);

  // Low bar count warning
  const [showLowBarWarning, setShowLowBarWarning] = useState(false);
  const [pendingParsedData, setPendingParsedData] = useState<{
    data: DataPoint[];
    normalizedFilename: string;
    normalizedJson: string;
    file: File;
    warnings?: string[];
  } | null>(null);

  // Date format prompt (for ambiguous DD-MM-YYYY vs MM-DD-YYYY)
  const [showDateFormatPrompt, setShowDateFormatPrompt] = useState(false);
  const [pendingDateFormatFile, setPendingDateFormatFile] =
    useState<File | null>(null);
  const [ambiguousSampleDates, setAmbiguousSampleDates] = useState<string[]>(
    [],
  );

  // Parameter states
  type ParameterValue = number | boolean | [number, number];
  const [parameters, setParameters] = useState<Record<string, ParameterValue>>({
    neighborscount: [5, 200],
    maxbarsback: [1000, 8000],
    emaperiod: [10, 600],
    smaperiod: [20, 600],
    lookbackwindow: [3, 20],
    relativeweight: [1, 50],
    regressionlevel: [5, 75],
    crossoverlag: [2, 8],
    regimethreshold: [-2.5, 2.5],
    adxthreshold: [20, 40],
    usevolatilityfilter: false,
    useregimefilter: true,
    useadxfilter: false,
    usesmafilter: true,
    useemafilter: false,
    usedynamicexits: false,
  });

  // Track which parameters are pinned
  const [pinnedParameters, setPinnedParameters] = useState<
    Record<string, boolean>
  >({});

  // Optimization settings state
  const [minimumTrades, setMinimumTrades] = useState(10);
  const [minimumTradesInput, setMinimumTradesInput] = useState("10"); // String state for input field
  const [objectiveFunction, setObjectiveFunction] = useState<
    "sortino" | "sharpe"
  >("sortino");
  const [samplerSeed, setSamplerSeed] = useState<number | null>(null); // null = random, number = fixed seed
  const [samplerSeedInput, setSamplerSeedInput] = useState(""); // String state for input field
  const [parallelCount, setParallelCount] = useState(1); // Number of parallel study instances

  const [features, setFeatures] = useState<Feature[]>(() =>
    clampFeatureSlots(
      [
        {
          id: "1",
          name: "RSI",
          period: 14,
          icon: <Activity className="w-4 h-4" />,
          color: "from-indigo-500 to-indigo-600",
        },
        {
          id: "2",
          name: "WT",
          period: 10,
          period2: 11,
          icon: <Activity className="w-4 h-4" />,
          color: "from-blue-500 to-blue-600",
        },
        {
          id: "3",
          name: "CCI",
          period: 20,
          icon: <Activity className="w-4 h-4" />,
          color: "from-cyan-500 to-cyan-600",
        },
        {
          id: "4",
          name: "ADX",
          period: 20,
          icon: <Activity className="w-4 h-4" />,
          color: "from-emerald-500 to-emerald-600",
        },
        {
          id: "5",
          name: "RSI",
          period: 9,
          icon: <Activity className="w-4 h-4" />,
          color: "from-indigo-500 to-indigo-600",
        },
      ],
      FALLBACK_SLOT_DEFAULTS,
    ),
  );

  // AIE-531 Phase 6: per-feature-param pin/range UI state (empty -> every param pinned at its slot
  // scalar, so default studies stay byte-identical). Populated by the FeatureBuilder range controls.
  const [featureParamState, setFeatureParamState] = useState<FeatureParamState>(
    {},
  );

  // Track completed sections
  const [completedSections, setCompletedSections] = useState<string[]>([]);

  // Track if we've already processed prefilled config (prevents duplicate toasts in React Strict Mode)
  const hasProcessedPrefilledConfig = useRef(false);

  // Debug: Track file state changes
  useEffect(() => {
    logger.info("🔧 DEBUG: File state changed", {
      hasFile: !!file,
      fileName: file?.name,
      fileSize: file?.size,
      hasData: !!data,
      dataLength: data?.length,
      completedSections,
    });
  }, [file, data, completedSections]);

  // Add auth check effect
  useEffect(() => {
    if (!authLoading && !aiewToken) {
      logger.error("NewStudyModern: No token available, redirecting to login");
      addToast({
        title: "Authentication required",
        description: "Please sign in to create a new study",
        variant: "error",
      });
      login(); // Redirect to AIEW login instead of defunct /login
    }
  }, [authLoading, aiewToken, login, addToast]);

  // Handle prefilled config from AI insights
  useEffect(() => {
    const prefilledConfig = location.state?.prefilledConfig;
    const sourceStudyId = location.state?.sourceStudyId;

    // Prevent duplicate execution in React Strict Mode
    if (prefilledConfig && !hasProcessedPrefilledConfig.current) {
      hasProcessedPrefilledConfig.current = true;
      logger.info("Pre-filling study form from AI recommendations", {
        sourceStudyId,
        studyName: prefilledConfig.study_name,
      });

      // Set basic study info
      setStudyName(prefilledConfig.study_name);
      const { max: maxTrials } = config.optimization.trialLimits;
      const clampedTrials = Math.max(
        1,
        Math.min(prefilledConfig.n_trials, maxTrials),
      );
      setNTrials(clampedTrials);
      setNTrialsInput(clampedTrials.toString());
      // != null catches both null and undefined while allowing 0 (a valid original value)
      if (prefilledConfig.original_n_trials != null) {
        const wasAboveMax = prefilledConfig.original_n_trials > maxTrials;
        addToast({
          title: "Trial count adjusted",
          description: wasAboveMax
            ? `The recommended ${prefilledConfig.original_n_trials.toLocaleString()} trials was reduced to the maximum of ${maxTrials.toLocaleString()}.`
            : `The recommended ${prefilledConfig.original_n_trials.toLocaleString()} trials was raised to the minimum allowed.`,
          variant: "info",
          duration: 6000,
        });
      }
      setSymbol(prefilledConfig.symbol);
      setTimeframe(normalizeTimeframe(prefilledConfig.timeframe));
      setMinimumTrades(prefilledConfig.minimum_trades);
      setMinimumTradesInput(prefilledConfig.minimum_trades.toString());
      setObjectiveFunction(prefilledConfig.objective_function);

      // Set data from source study (if available)
      if (
        prefilledConfig.original_data &&
        prefilledConfig.original_data.length > 0
      ) {
        logger.info("🔧 DEBUG: Starting to set prefilled data", {
          dataLength: prefilledConfig.original_data.length,
          sourceFilename: prefilledConfig.source_filename,
        });

        setData(prefilledConfig.original_data as DataPoint[]);
        setSourceFilename(prefilledConfig.source_filename || null);
        setUsingSampleData(false); // Not using sample data anymore

        // Create a synthetic File object to show in the FileUpload component
        // This makes the UI show that data is loaded
        const dataJson = JSON.stringify(prefilledConfig.original_data);
        const blob = new Blob([dataJson], { type: "application/json" });
        const syntheticFile = new File(
          [blob],
          prefilledConfig.source_filename || "study_data.json",
          { type: "application/json" },
        );

        logger.info("🔧 DEBUG: Created synthetic file", {
          fileName: syntheticFile.name,
          fileSize: syntheticFile.size,
          fileType: syntheticFile.type,
        });

        setFile(syntheticFile);

        // Mark data section as complete
        setCompletedSections((prev) => {
          const updated = prev.includes("data") ? prev : [...prev, "data"];
          logger.info("🔧 DEBUG: Updated completed sections", {
            previous: prev,
            updated: updated,
          });
          return updated;
        });

        logger.info(
          `✅ Pre-filled data from source study: ${prefilledConfig.source_filename} (${prefilledConfig.original_data.length} data points)`,
        );
      }

      // Set features
      const mappedFeatures: Feature[] = prefilledConfig.features.map(
        (
          f: { name: string; period: number; period2?: number },
          idx: number,
        ) => ({
          id: `prefilled_${idx}`,
          name: f.name,
          period: f.period,
          period2: f.period2,
          icon: <Activity className="w-4 h-4" />,
          color: "from-indigo-500 to-indigo-600",
        }),
      );
      setFeatures(clampFeatureSlots(mappedFeatures, slotDefaults));
      setFeatureParamState({}); // fresh feature set -> reset range UI state

      // Set parameters
      const newParameters: Record<string, ParameterValue> = {};
      const newPinnedParameters: Record<string, boolean> = {};

      Object.entries(prefilledConfig.parameters).forEach(([key, value]) => {
        if (typeof value === "boolean") {
          // Boolean parameter
          newParameters[key] = value;
        } else if (
          value &&
          typeof value === "object" &&
          "low" in value &&
          "high" in value
        ) {
          // Range parameter
          const rangeValue = value as {
            low: number;
            high: number;
            step?: number;
          };
          if (rangeValue.low === rangeValue.high) {
            // Fixed parameter (pinned)
            newParameters[key] = rangeValue.low;
            newPinnedParameters[key] = true;
          } else {
            // Range parameter
            newParameters[key] = [rangeValue.low, rangeValue.high];
          }
        }
      });

      // Apply fixed boolean parameters from fixed_parameters
      Object.entries(prefilledConfig.fixed_parameters || {}).forEach(
        ([key, value]) => {
          newParameters[key] = value as ParameterValue;
          newPinnedParameters[key] = true;
        },
      );

      setParameters(newParameters);
      setPinnedParameters(newPinnedParameters);

      // Show success toast
      const dataIncluded =
        prefilledConfig.original_data &&
        prefilledConfig.original_data.length > 0;
      addToast({
        title: "Study Configuration Loaded",
        description: `Pre-filled from AI recommendations${sourceStudyId ? ` (Study: ${sourceStudyId})` : ""}${dataIncluded ? ` including ${prefilledConfig.original_data!.length.toLocaleString()} data points` : ""}. Review and adjust as needed.`,
        variant: "success",
        duration: 8000,
      });

      // Clear the location state so it doesn't persist on refresh
      window.history.replaceState({}, document.title);
    }
    // slotDefaults: re-runs are no-ops (guarded by hasProcessedPrefilledConfig); listed to
    // satisfy exhaustive-deps now that prefill clamps via clampFeatureSlots(slotDefaults).
  }, [location.state, addToast, slotDefaults]);

  // Define the steps for the progress rail
  const steps = [
    {
      id: "data",
      label: "Upload Data",
      description: "Import your TradingView data",
    },
    {
      id: "parameters",
      label: "Set Parameters",
      description: "Fine-tune optimization settings",
    },
    {
      id: "features",
      label: "Feature Engineering",
      description: "Select technical indicators",
    },
    {
      id: "preview",
      label: "Review & Launch",
      description: "Preview and start optimization",
    },
  ];

  const createStudyMutation = useMutation({
    mutationFn: async (request: OptimizationRequest) => {
      // Session check is redundant - user cannot reach this page without a session
      // (enforced by lines 189-202 and 681-684)
      logger.info("NewStudyModern: Creating study", {
        hasToken: !!aiewToken,

        studyName: request.study_name,
      });

      return api.startOptimization(request);
    },
    onSuccess: (response) => {
      // Invalidate studies queries to refresh the studies list immediately
      queryClient.invalidateQueries({ queryKey: ["studies"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-studies"] });
      // Invalidate userLimits to show reserved credits immediately
      queryClient.invalidateQueries({ queryKey: ["userLimits"] });

      logger.info("NewStudyModern: Study created, invalidated query cache", {
        studyId: response.study_id,
      });

      navigate("/active-studies", {
        state: { message: `Study ${response.study_id} started successfully!` },
      });
    },
    onError: (error: Error) => {
      logger.error("NewStudyModern: Study creation failed", error);
      addToast({
        title: "Failed to start study",
        description:
          error.message ||
          "An unexpected error occurred. Please check the console for details.",
        variant: "error",
      });
      console.error("Study creation failed:", error);
    },
  });

  // Helper function to mark a section as complete
  const markSectionComplete = (sectionId: string) => {
    setCompletedSections((prev) => {
      if (!prev.includes(sectionId)) {
        return [...prev, sectionId];
      }
      return prev;
    });
  };

  // Helper function to mark all sections up to and including the target as complete
  const markSectionsUpToComplete = (targetSectionId: string) => {
    const targetIndex = steps.findIndex((s) => s.id === targetSectionId);
    if (targetIndex === -1) return;

    const sectionsToComplete = steps.slice(0, targetIndex).map((s) => s.id);
    setCompletedSections(sectionsToComplete);
  };

  // Helper function to apply parsed data to state
  const applyParsedData = (parsedData: {
    data: DataPoint[];
    normalizedFilename: string;
    normalizedJson: string;
    file: File;
    warnings?: string[];
  }) => {
    setFile(parsedData.file);
    setData(parsedData.data);
    setSourceFilename(parsedData.normalizedFilename);
    setNormalizedJson(parsedData.normalizedJson);
    setPendingParsedData(null);

    if (parsedData.warnings?.length) {
      addToast({
        title: "Data imported with warnings",
        description: parsedData.warnings.join("; "),
        variant: "warning",
      });
    }

    // Honest early warning: tell the user up front when a dataset is likely too large to
    // start a study, instead of letting them configure everything and fail at submit.
    const payload = assessOptimizationPayload(parsedData.data.length);
    if (payload.exceedsLimit) {
      addToast({
        title: "Large dataset may not start",
        description: `${parsedData.data.length.toLocaleString()} candles (~${payload.estimatedFormatted}) exceeds the ${payload.limitFormatted} limit for starting a study. Use a higher timeframe or a shorter date range.`,
        variant: "warning",
        duration: 10000,
      });
    }
  };

  // Handler for when user proceeds despite low bar count
  const handleLowBarWarningProceed = () => {
    if (pendingParsedData) {
      applyParsedData(pendingParsedData);
      logger.info("User proceeded with low bar count data", {
        barCount: pendingParsedData.data.length,
        recommendedMin: getRecommendedMinBars(),
      });
    }
  };

  // Handler for when user cancels due to low bar count
  const handleLowBarWarningCancel = () => {
    logger.info("User cancelled upload due to low bar count", {
      barCount: pendingParsedData?.data.length,
    });
    setPendingParsedData(null);
    setFile(null);
    setData(null);
    setTimeframe(null);
    setSourceFilename(null);
    setNormalizedJson(null);
  };

  // Handler for when user selects a date format for ambiguous dates
  const handleDateFormatSelected = async (format: DateFormat) => {
    if (!pendingDateFormatFile) return;
    setShowDateFormatPrompt(false);
    setIsValidatingFile(true);
    try {
      const parsedData = await parseDataFile(pendingDateFormatFile, {
        dateFormat: format,
      });
      // Infer timeframe from bar intervals
      const inferredTf = normalizeTimeframe(
        inferTimeframe(parsedData.data) ?? "",
      );
      setTimeframe(inferredTf);
      if (
        isBarCountLow(parsedData.data.length) &&
        !hasUserDismissedLowBarWarning()
      ) {
        setPendingParsedData({
          data: parsedData.data,
          normalizedFilename: parsedData.normalizedFilename,
          normalizedJson: parsedData.normalizedJson,
          file: pendingDateFormatFile,
          warnings: parsedData.warnings,
        });
        setShowLowBarWarning(true);
      } else {
        applyParsedData({
          data: parsedData.data,
          normalizedFilename: parsedData.normalizedFilename,
          normalizedJson: parsedData.normalizedJson,
          file: pendingDateFormatFile,
          warnings: parsedData.warnings,
        });
        setFileValidationError(undefined);
      }
    } catch (error) {
      console.error("Error parsing file with date format:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to parse file.";
      setFileValidationError(errorMessage);
    } finally {
      setIsValidatingFile(false);
      setPendingDateFormatFile(null);
      setAmbiguousSampleDates([]);
    }
  };

  const handleFileAccepted = async (acceptedFile: File) => {
    setFile(acceptedFile);
    setUsingSampleData(false);
    setSampleDataset(null);
    setSourceFilename(acceptedFile.name);
    setNormalizedJson(null);
    setFileValidationError(undefined);
    setIsValidatingFile(true);

    // Generate study name from file name
    const fileNameWithoutExt = acceptedFile.name.replace(/\.[^/.]+$/, ""); // Remove extension
    // Normalize: replace commas and spaces with underscores, remove special chars
    const normalizedFileName = fileNameWithoutExt
      .replace(/[,\s]+/g, "_") // Replace commas and spaces with underscores
      .replace(/_{2,}/g, "_") // Replace multiple underscores with single underscore
      .replace(/[^\w-]/g, ""); // Remove any remaining special characters except hyphens
    const timestamp = new Date()
      .toISOString()
      .slice(0, 19)
      .replace(/[:-]/g, ""); // Format: 20240115T143025
    const uniqueId = timestamp.replace("T", "_"); // Format: YYYYMMDD_HHMMSS for standard datetime
    setStudyName(`${normalizedFileName}_${uniqueId}`);

    // Parse symbol: take everything before the first comma
    if (acceptedFile.name.includes(",")) {
      const symbolPart = acceptedFile.name.split(",")[0]?.trim();
      if (symbolPart) {
        setSymbol(symbolPart);
      }
    } else if (acceptedFile.name.includes("_")) {
      // Fallback to underscore parsing if no comma
      const parts = acceptedFile.name.split("_");
      if (parts.length >= 2) {
        setSymbol(`${parts[0]}_${parts[1]}`);
      }
    }

    try {
      const parsedData = await parseDataFile(acceptedFile);
      // Infer timeframe from bar intervals
      const inferredTf = normalizeTimeframe(
        inferTimeframe(parsedData.data) ?? "",
      );
      setTimeframe(inferredTf);

      // Check if bar count is low and user hasn't dismissed the warning
      if (
        isBarCountLow(parsedData.data.length) &&
        !hasUserDismissedLowBarWarning()
      ) {
        // Store parsed data and show warning dialog
        setPendingParsedData({
          data: parsedData.data,
          normalizedFilename: parsedData.normalizedFilename,
          normalizedJson: parsedData.normalizedJson,
          file: acceptedFile,
          warnings: parsedData.warnings,
        });
        setShowLowBarWarning(true);
        setIsValidatingFile(false);
        logger.info("Low bar count detected, showing warning", {
          barCount: parsedData.data.length,
          recommendedMin: getRecommendedMinBars(),
        });
      } else {
        // Proceed directly - either bar count is sufficient or user dismissed warning
        applyParsedData({
          data: parsedData.data,
          normalizedFilename: parsedData.normalizedFilename,
          normalizedJson: parsedData.normalizedJson,
          file: acceptedFile,
          warnings: parsedData.warnings,
        });
        setIsValidatingFile(false);
        setFileValidationError(undefined);
      }
    } catch (error) {
      // Handle ambiguous date format — prompt user to select
      if (error instanceof AmbiguousDateFormatError) {
        // Clear any previously loaded data so stale results can't be used
        // if the user dismisses the dialog without selecting a format
        setData(null);
        setNormalizedJson(null);
        setPendingParsedData(null);
        setPendingDateFormatFile(acceptedFile);
        setAmbiguousSampleDates(error.sampleDates);
        setShowDateFormatPrompt(true);
        setIsValidatingFile(false);
        return;
      }
      console.error("Error parsing file:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to parse file. Please check the file format.";
      setFileValidationError(errorMessage);
      setFile(null);
      setData(null);
      setSourceFilename(null);
      setNormalizedJson(null);
      setIsValidatingFile(false);
      logger.error("File validation failed", { error: errorMessage });
    }
  };

  const handleUseSampleDataset = async (datasetId: string) => {
    setIsLoadingSampleData(true);
    try {
      const { data: sample, meta } = await loadSampleData(datasetId);
      setData(sample);
      setFile(null);
      setUsingSampleData(true);
      const ds = SAMPLE_DATASETS.find((d) => d.id === datasetId) || null;
      setSampleDataset(ds);
      const inferredSource =
        meta?.source || ds?.url?.split("/").pop() || ds?.displayName;
      setSourceFilename(inferredSource || null);
      setNormalizedJson(JSON.stringify(sample, null, 2));
      if (meta?.symbol) setSymbol(meta.symbol);
      const normalizedTf = normalizeTimeframe(
        meta?.timeframe ?? ds?.timeframe ?? "D1",
      );
      setTimeframe(normalizedTf);
      if (!studyName) {
        const ts =
          meta?.end || new Date().toISOString().slice(0, 10).replace(/-/g, "");
        const tfLabel = (normalizedTf ?? "unknown").toLowerCase();
        setStudyName(
          `sample_${(meta?.symbol || ds?.symbol || "BTC_USD").toLowerCase()}_${tfLabel}_${ts}`,
        );
      }
      markSectionComplete("data");
    } catch (e) {
      addToast({
        title: "Could not load sample data",
        description: e instanceof Error ? e.message : "Please try again",
        variant: "error",
      });
    } finally {
      // Always close the modal and stop loading, regardless of success or failure
      setShowSampleDataDialog(false);
      setIsLoadingSampleData(false);
    }
  };

  const handleSampleDataSelect = (dataset: SampleDataset) => {
    void handleUseSampleDataset(dataset.id);
  };

  const handleParameterChange = (key: string, value: ParameterValue) => {
    setParameters((prev) => ({ ...prev, [key]: value }));
  };

  const handlePinnedChange = (key: string, isPinned: boolean) => {
    setPinnedParameters((prev) => ({ ...prev, [key]: isPinned }));
  };

  const handleLcSettingsImport = (result: LcSettingsImportResult) => {
    setParameters(result.parameters as Record<string, ParameterValue>);
    setPinnedParameters(result.pinnedParameters);
    setFeatures(
      clampFeatureSlots(
        result.features.map((f, idx) => ({
          id: `imported_${idx}`,
          name: f.name,
          period: f.period,
          period2: f.period2,
          icon: <Activity className="w-4 h-4" />,
          color: "from-indigo-500 to-indigo-600",
        })),
        slotDefaults,
      ),
    );
    setFeatureParamState({}); // imported feature set -> reset range UI state
  };

  const handleSubmit = () => {
    if (!data || timeframe === null) return;

    // Process parameters based on pinned state
    const processedParameters: Record<string, unknown> = {};

    // Handle range parameters
    const rangeParams = [
      "neighborscount",
      "maxbarsback",
      "emaperiod",
      "smaperiod",
      "lookbackwindow",
      "relativeweight",
      "regressionlevel",
      "crossoverlag",
      "regimethreshold",
      "adxthreshold",
    ];

    rangeParams.forEach((param) => {
      // AIE-235 FIX: Skip numeric parameters whose associated boolean filter is pinned OFF
      // Uses shared config from @/config/parameterDependencies.ts
      const associatedFilter = NUMERIC_PARAM_TO_FILTER[param];
      if (
        associatedFilter &&
        pinnedParameters[associatedFilter] &&
        parameters[associatedFilter] === false
      ) {
        // Filter is pinned OFF - don't include this numeric parameter in optimization
        return;
      }

      if (pinnedParameters[param]) {
        // If pinned, use the single value
        processedParameters[param] =
          typeof parameters[param] === "number"
            ? parameters[param]
            : Array.isArray(parameters[param])
              ? (parameters[param] as [number, number])[0]
              : parameters[param]; // Use first value if still a range
      } else {
        // If not pinned, use the range. The backend expects certain params
        // with a '_range' suffix.
        if (param === "neighborscount" || param === "maxbarsback") {
          processedParameters[`${param}_range`] = parameters[param];
        } else {
          // CORRECTED: Send the full [min, max] array for other ranged parameters
          processedParameters[param] = parameters[param];
        }
      }
    });

    // Handle boolean parameters
    const booleanParams = [
      "usedynamicexits",
      "useemafilter",
      "usesmafilter",
      "usevolatilityfilter",
      "useregimefilter",
      "useadxfilter",
    ];

    booleanParams.forEach((param) => {
      // Only include boolean parameters if they're NOT pinned, or include them in pinnedBooleans
      if (!pinnedParameters[param]) {
        // Not pinned - should be optimized
        processedParameters[param] = parameters[param];
      }
      // If pinned, we'll handle it separately in pinnedBooleans
    });

    // Collect pinned boolean parameters with their values
    const pinnedBooleans: Record<string, boolean> = {};
    booleanParams.forEach((param) => {
      if (pinnedParameters[param]) {
        pinnedBooleans[param] = parameters[param] as boolean;
      }
    });

    // Preserve pinned numeric values whose parent filter is OFF.
    // These were skipped from optimization above (AIE-235) but must be
    // recorded in [fixed_parameters] so exports reflect what the user set.
    rangeParams.forEach((param) => {
      const associatedFilter = NUMERIC_PARAM_TO_FILTER[param];
      if (
        associatedFilter &&
        pinnedParameters[associatedFilter] &&
        parameters[associatedFilter] === false &&
        pinnedParameters[param]
      ) {
        const value =
          typeof parameters[param] === "number"
            ? parameters[param]
            : Array.isArray(parameters[param])
              ? (parameters[param] as [number, number])[0]
              : parameters[param];
        (pinnedBooleans as Record<string, unknown>)[param] = value;
      }
    });

    // Add optimization settings
    processedParameters.minimum_trades = minimumTrades;
    processedParameters.objective_function = objectiveFunction;

    // Generate TOML config. Codex PR review: build the [[features]] blocks from the SAME derived
    // slot state used for request.parameters (below) — using the raw `features` scalars leaves
    // flag-ON pinned-slider edits stale in the stored/downloaded/AI-prefilled TOML. The rangeEnabled
    // gate mirrors buildOptimizationRequest so the TOML and parameters channels can't diverge.
    const featureSlotStates = deriveSlotStates(
      features,
      featureRangeEnabled ? featureParamState : {},
      featureTypes,
    );
    const tomlConfig = generateTomlConfig({
      config: { ...processedParameters, n_trials: nTrials },
      studyName:
        studyName ||
        `lorentzian_${symbol.toLowerCase()}_${timeframe.toLowerCase()}`,
      symbol,
      timeframe,
      features: slotStatesToTomlFeatures(featureSlotStates),
      pinnedBooleans,
      samplerSeed,
    });

    // AIE-531 (routing fix, UNCONDITIONAL): buildOptimizationRequest merges the chosen feature
    // config into request.parameters (the live channel the worker consumes) — NEVER
    // request.parameter_info (the `or` there would drop every strategy param). Conditional: an
    // all-default study emits no fN_* keys, so the payload stays byte-identical.
    const request: OptimizationRequest = buildOptimizationRequest({
      studyName,
      nTrials,
      symbol,
      timeframe,
      data,
      strategyParameters: processedParameters,
      features,
      featureParamState,
      rangeEnabled: featureRangeEnabled,
      slotDefaults,
      featureTypes,
      tomlConfig,
      minimumTrades,
      objectiveFunction,
      samplerSeed,
      parallelCount,
    });

    // Honest pre-flight guard: block submit with a clear message when the dataset is too
    // large to send, rather than letting it fail downstream with an opaque 413.
    const payloadAssessment = assessOptimizationPayload(data.length);
    if (payloadAssessment.exceedsLimit) {
      addToast({
        title: "Dataset too large to start study",
        description: `Your dataset has ${data.length.toLocaleString()} candles (~${payloadAssessment.estimatedFormatted}), which exceeds the ${payloadAssessment.limitFormatted} limit for starting a study. Use a higher timeframe or a shorter date range, then try again.`,
        variant: "error",
        duration: 10000,
      });
      return;
    }

    createStudyMutation.mutate(request);
  };

  const isDataReady = !!data;
  const allConfigReady =
    isDataReady && features.length >= 2 && timeframe !== null;

  // Debug logging for button visibility
  useEffect(() => {
    logger.info("🔍 [NEW STUDY DEBUG] allConfigReady state check:", {
      allConfigReady,
      isDataReady,
      hasData: !!data,
      dataLength: data?.length,
      featuresLength: features.length,
      activeSection,
      isPending: createStudyMutation.isPending,
    });
  }, [
    allConfigReady,
    isDataReady,
    data,
    features.length,
    activeSection,
    createStudyMutation.isPending,
  ]);

  // Determine the primary action based on current section
  const getPrimaryAction = () => {
    switch (activeSection) {
      case "data":
        return {
          label: "Next",
          disabled: !isDataReady,
          onClick: () => {
            markSectionComplete("data");
            setActiveSection("parameters");
          },
          icon: <FileText className="w-4 h-4" />,
        };
      case "parameters":
        return {
          label: "Next",
          disabled: false,
          onClick: () => {
            markSectionsUpToComplete("features");
            markSectionComplete("parameters");
            setActiveSection("features");
          },
          icon: <Settings className="w-4 h-4" />,
        };
      case "features":
        return {
          label: "Next",
          disabled: features.length < 2,
          onClick: () => {
            markSectionsUpToComplete("preview");
            markSectionComplete("features");
            setActiveSection("preview");
          },
          icon: <Activity className="w-4 h-4" />,
        };
      case "preview":
        return {
          label: createStudyMutation.isPending
            ? "Starting Study..."
            : "Start Optimization",
          disabled: !allConfigReady || createStudyMutation.isPending,
          onClick: handleSubmit,
          icon: createStudyMutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Rocket className="w-4 h-4" />
          ),
        };
      default:
        return null;
    }
  };

  const primaryAction = getPrimaryAction();

  // Show loading state while auth is loading
  if (authLoading) {
    return (
      <CandlestickLoader
        isLoading
        context="NewStudyModern:auth"
        containerClassName="w-full min-h-[70vh] px-6"
        messageClassName="text-slate-200"
        messages={NEW_STUDY_AUTH_MESSAGES}
      />
    );
  }

  // If not authenticated, return null (useEffect will redirect)
  if (!aiewToken) {
    return null;
  }

  return (
    <div
      className="w-full min-w-0 max-w-7xl mx-auto space-y-layout-sm pb-28 lg:pb-0"
      data-testid="optimize-form"
    >
      <PageHeader
        title="Create New Study"
        description="Configure and launch a new Lorentzian optimization study"
      />

      <SystemPausedBanner />

      <div className="grid min-w-0 lg:grid-cols-[300px_minmax(0,1fr)] gap-layout-sm">
        {/* Progress Rail - Sticky position */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="hidden lg:block"
        >
          <div className="sticky top-8">
            <GlassCard className="p-card shadow-2xl shadow-brand-blue/10 border-brand-blue/20">
              <h2 className="text-lg font-semibold mb-6">Progress</h2>
              <ProgressRail
                steps={steps}
                currentStep={activeSection}
                completedSteps={completedSections}
                onStepClick={(stepId) => {
                  const stepIndex = steps.findIndex((s) => s.id === stepId);
                  const currentIndex = steps.findIndex(
                    (s) => s.id === activeSection,
                  );

                  // Navigate to the clicked section
                  setActiveSection(stepId);

                  // If navigating forward, mark all previous sections as complete
                  if (stepIndex > currentIndex) {
                    markSectionsUpToComplete(stepId);
                  } else if (stepIndex < currentIndex) {
                    // If navigating backward, only keep sections before the target as complete
                    const sectionsToKeep = steps
                      .slice(0, stepIndex)
                      .map((s) => s.id);
                    setCompletedSections(sectionsToKeep);
                  }
                }}
              />
            </GlassCard>
          </div>
        </motion.div>

        {/* Main Content Area */}
        <div className="min-w-0 space-y-layout-sm">
          <Accordion
            type="single"
            value={activeSection}
            onValueChange={(value) => {
              setActiveSection(value);
              // Handle navigation properly
              const currentIndex = steps.findIndex(
                (s) => s.id === activeSection,
              );
              const newIndex = steps.findIndex((s) => s.id === value);

              if (newIndex > currentIndex) {
                // When navigating forward via accordion, mark all previous sections as complete
                markSectionsUpToComplete(value);
              } else if (newIndex < currentIndex) {
                // When navigating backward, only keep sections before the target as complete
                const sectionsToKeep = steps
                  .slice(0, newIndex)
                  .map((s) => s.id);
                setCompletedSections(sectionsToKeep);
              }
            }}
            className="min-w-0 space-y-4"
          >
            {/* Data Upload Section */}
            <AccordionItem
              value="data"
              className="bg-dark-800/30 rounded-lg border border-dark-700/50 overflow-visible"
            >
              <AccordionTrigger className="px-card py-layout-xs">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5" />
                  <span className="font-semibold">Data Upload</span>
                  {completedSections.includes("data") && (
                    <CheckCircle className="w-5 h-5 text-green-500 ml-2" />
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="p-card-sm sm:p-card pt-card-sm grid min-w-0 gap-layout-sm lg:grid-cols-2">
                  <GlassCard>
                    <GlassCardHeader className="pb-4">
                      <div className="flex items-center justify-between">
                        <GlassCardTitle className="text-xl">
                          Upload File
                        </GlassCardTitle>
                        {usingSampleData && <SampleDataBadge />}
                      </div>
                      <GlassCardDescription className="mt-1">
                        Import your TradingView file or try sample data
                      </GlassCardDescription>
                    </GlassCardHeader>
                    <GlassCardContent className="pt-0">
                      <FileUpload
                        onFileAccepted={handleFileAccepted}
                        onFileRemoved={() => {
                          logger.info("🗑️ File removed by user");
                          setFile(null);
                          setData(null);
                          setTimeframe(null);
                          setUsingSampleData(false);
                          setSampleDataset(null);
                          setFileValidationError(undefined);
                          setIsValidatingFile(false);
                        }}
                        {...(file ? { acceptedFile: file } : {})}
                        error={fileValidationError}
                        isValidating={isValidatingFile}
                      />
                      {!file && !data && (
                        <div className="mt-4">
                          <div className="flex items-center justify-center">
                            <button
                              type="button"
                              onClick={() => setShowSampleDataDialog(true)}
                              className="group inline-flex items-center gap-2 rounded-lg px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-brand-purple/20 to-brand-blue/20 hover:from-brand-purple/40 hover:to-brand-blue/40 border border-brand-purple/40 hover:border-brand-purple/70 hover:shadow-lg hover:shadow-brand-purple/20 hover:scale-105 active:scale-95 transition-all duration-200 cursor-pointer"
                            >
                              <Sparkles className="w-4 h-4 group-hover:text-brand-purple-light transition-colors" />
                              Just test it out with sample data
                            </button>
                          </div>
                        </div>
                      )}
                      {data && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-4 p-4 rounded-lg bg-gradient-to-r from-brand-blue/10 to-brand-blue-dark/10 border border-brand-blue/20"
                        >
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className="min-w-0 flex flex-col">
                              <span className="text-muted-foreground text-xs mb-1">
                                Data Points
                              </span>
                              <span className="font-semibold text-white truncate">
                                {data.length.toLocaleString()}
                              </span>
                            </div>
                            <div className="min-w-0 flex flex-col">
                              <span className="text-muted-foreground text-xs mb-1">
                                Status
                              </span>
                              <span className="font-semibold text-green-500 truncate">
                                Ready
                              </span>
                            </div>
                          </div>
                          {/* Timeframe selector */}
                          <div className="mt-3 pt-3 border-t border-brand-blue/20">
                            <span className="text-muted-foreground text-xs mb-2 block">
                              Timeframe
                            </span>
                            <TimeframeSelector
                              value={timeframe}
                              onChange={(tf) => setTimeframe(tf)}
                            />
                          </div>
                          {usingSampleData && sampleDataset && (
                            <div className="mt-3 pt-3 border-t border-brand-blue/20 text-xs text-muted-foreground">
                              <span className="truncate block">
                                Using {sampleDataset.displayName} sample data (
                                {sampleDataset.timeframe}). Replace anytime by
                                uploading a file.
                              </span>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </GlassCardContent>
                  </GlassCard>

                  <GlassCard>
                    <GlassCardHeader className="pb-4">
                      <GlassCardTitle className="text-xl">
                        Study Configuration
                      </GlassCardTitle>
                      <GlassCardDescription className="mt-1">
                        Configure your optimization study settings
                      </GlassCardDescription>
                    </GlassCardHeader>
                    <GlassCardContent className="pt-0 space-y-5">
                      {/* Study Name - Full Width */}
                      <div>
                        <Label
                          htmlFor="study-name"
                          className="text-sm font-medium"
                        >
                          Study Name
                        </Label>
                        <Input
                          id="study-name"
                          placeholder="Auto-generated if empty"
                          value={studyName}
                          onChange={(e) => setStudyName(e.target.value)}
                          className="mt-1.5"
                          data-testid="study-name-input"
                        />
                      </div>

                      {/* Number of Trials and Symbol - Two Column Grid */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label
                            htmlFor="n-trials"
                            className="text-sm font-medium"
                          >
                            Number of Trials
                          </Label>
                          <Input
                            id="n-trials"
                            type="number"
                            min={1}
                            max={config.optimization.trialLimits.max}
                            value={nTrialsInput}
                            onChange={(e) => {
                              const value = e.target.value;
                              setNTrialsInput(value);

                              // Clamp submission value to max; display syncs on blur
                              const parsed = parseInt(value);
                              if (!isNaN(parsed) && parsed > 0) {
                                const { max: maxTrials } =
                                  config.optimization.trialLimits;
                                setNTrials(Math.min(parsed, maxTrials));
                              }
                            }}
                            onBlur={(e) => {
                              const value = e.target.value;
                              const parsed = parseInt(value);
                              const { max: maxTrials, default: defaultTrials } =
                                config.optimization.trialLimits;

                              // If field is empty or invalid, reset to default
                              if (
                                value === "" ||
                                isNaN(parsed) ||
                                parsed <= 0
                              ) {
                                setNTrialsInput(defaultTrials.toString());
                                setNTrials(defaultTrials);
                              } else {
                                // Clamp to valid range and sync display
                                const clamped = Math.min(
                                  Math.max(parsed, 1),
                                  maxTrials,
                                );
                                setNTrialsInput(clamped.toString());
                                setNTrials(clamped);
                              }
                            }}
                            className="mt-1.5"
                            data-testid="trial-count-input"
                          />
                        </div>
                        <div>
                          <Label
                            htmlFor="symbol"
                            className="text-sm font-medium"
                          >
                            Symbol
                          </Label>
                          <Input
                            id="symbol"
                            value={symbol}
                            onChange={(e) => setSymbol(e.target.value)}
                            className="mt-1.5"
                            data-testid="symbol-input"
                          />
                        </div>
                      </div>

                      {/* Minimum Trades and Random Seed - Two Column Grid */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2.5">
                          <div className="flex items-center gap-2">
                            <Label
                              htmlFor="minimum-trades"
                              className="text-sm font-medium"
                            >
                              Minimum Trades
                            </Label>
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    className="inline-flex items-center"
                                  >
                                    <Info className="w-4 h-4 text-muted-foreground hover:text-brand-purple transition-colors" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="right"
                                  align="start"
                                  sideOffset={8}
                                  className="bg-dark-800 text-white p-3 rounded-lg max-w-xs border border-dark-700 z-[100] shadow-2xl"
                                >
                                  <p className="text-sm leading-relaxed">
                                    Trials with fewer trades than this threshold
                                    will be pruned. Lower values allow more
                                    parameter exploration but may lead to less
                                    reliable results. Higher values ensure
                                    statistical significance but may limit the
                                    search space.{" "}
                                    <strong>Default: 10 trades.</strong>
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <Input
                            id="minimum-trades"
                            type="number"
                            min={1}
                            max={100}
                            value={minimumTradesInput}
                            onChange={(e) => {
                              const value = e.target.value;
                              setMinimumTradesInput(value);

                              // Only update minimumTrades if it's a valid positive number
                              const parsed = parseInt(value);
                              if (
                                !isNaN(parsed) &&
                                parsed >= 1 &&
                                parsed <= 100
                              ) {
                                setMinimumTrades(parsed);
                              }
                            }}
                            onBlur={(e) => {
                              const value = e.target.value;
                              const parsed = parseInt(value);

                              // If field is empty or invalid, reset to default
                              if (
                                value === "" ||
                                isNaN(parsed) ||
                                parsed < 1 ||
                                parsed > 100
                              ) {
                                setMinimumTradesInput("10");
                                setMinimumTrades(10);
                              } else {
                                // Ensure the input field shows the parsed value
                                setMinimumTradesInput(parsed.toString());
                                setMinimumTrades(parsed);
                              }
                            }}
                            className="mt-1.5"
                          />
                        </div>

                        <div className="space-y-2.5">
                          <div className="flex items-center gap-2">
                            <Label
                              htmlFor="sampler-seed"
                              className="text-sm font-medium"
                            >
                              Seed
                            </Label>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    className="inline-flex items-center"
                                  >
                                    <Info className="w-4 h-4 text-muted-foreground hover:text-brand-purple transition-colors" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent className="bg-dark-800 text-white p-3 rounded-lg max-w-xs border border-dark-700 shadow-2xl">
                                  <p className="text-sm leading-relaxed">
                                    Set a fixed seed for reproducible
                                    optimization results. Using the same seed
                                    with identical parameters will produce the
                                    same trial sequence. Leave empty for random
                                    sampling (non-reproducible).{" "}
                                    <strong>Example: 42</strong>
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <Input
                            id="sampler-seed"
                            type="number"
                            placeholder="Optional"
                            value={samplerSeedInput}
                            onChange={(e) => {
                              const value = e.target.value;
                              setSamplerSeedInput(value);

                              // Only update samplerSeed if it's a valid positive number
                              const parsed = parseInt(value);
                              if (!isNaN(parsed) && parsed > 0) {
                                setSamplerSeed(parsed);
                              } else if (value === "") {
                                setSamplerSeed(null);
                              }
                            }}
                            onBlur={(e) => {
                              const value = e.target.value;
                              const parsed = parseInt(value);

                              // If field is empty, keep it empty and set seed to null
                              if (value === "") {
                                setSamplerSeedInput("");
                                setSamplerSeed(null);
                              } else if (isNaN(parsed) || parsed <= 0) {
                                // If invalid, clear the field
                                setSamplerSeedInput("");
                                setSamplerSeed(null);
                              } else {
                                // Ensure the input field shows the parsed value
                                setSamplerSeedInput(parsed.toString());
                                setSamplerSeed(parsed);
                              }
                            }}
                          />
                        </div>
                      </div>

                      {/* Objective Function - Full Width */}
                      <div className="space-y-2.5">
                        <div className="flex items-center gap-2">
                          <Label
                            htmlFor="objective-function"
                            className="text-sm font-medium"
                          >
                            Objective Function
                          </Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  className="inline-flex items-center"
                                >
                                  <Info className="w-4 h-4 text-muted-foreground hover:text-brand-purple transition-colors" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent className="bg-dark-800 text-white p-3 rounded-lg max-w-xs border border-dark-700 shadow-2xl">
                                <p className="text-sm leading-relaxed">
                                  <strong>Sortino Ratio (default)</strong>{" "}
                                  focuses on downside risk and is better for
                                  strategies with asymmetric returns.{" "}
                                  <strong>Sharpe Ratio</strong> considers all
                                  volatility equally and is more traditional.
                                  Both are adjusted for trade confidence based
                                  on sample size.
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <Select
                          value={objectiveFunction}
                          onValueChange={(value: "sortino" | "sharpe") =>
                            setObjectiveFunction(value)
                          }
                        >
                          <SelectTrigger
                            id="objective-function"
                            className="w-full"
                          >
                            <SelectValue placeholder="Select objective function" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sortino">
                              <div className="flex items-center gap-2.5">
                                <div className="p-1.5 rounded bg-brand-purple/10">
                                  <TrendingUp className="w-3.5 h-3.5 text-brand-purple" />
                                </div>
                                <div className="text-left">
                                  <div className="font-medium text-sm">
                                    Sortino Ratio
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    Focuses on downside risk
                                  </div>
                                </div>
                              </div>
                            </SelectItem>
                            <SelectItem value="sharpe">
                              <div className="flex items-center gap-2.5">
                                <div className="p-1.5 rounded bg-brand-blue/10">
                                  <TrendingUp className="w-3.5 h-3.5 text-brand-blue" />
                                </div>
                                <div className="text-left">
                                  <div className="font-medium text-sm">
                                    Sharpe Ratio
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    Traditional risk-adjusted return
                                  </div>
                                </div>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Parallel Versions Selector */}
                      <ParallelSelector
                        value={parallelCount}
                        onChange={setParallelCount}
                        maxParallel={userLimits?.max_parallel_studies ?? 1}
                      />
                    </GlassCardContent>
                  </GlassCard>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Parameters Section */}
            <AccordionItem
              value="parameters"
              className="bg-dark-800/30 rounded-lg border border-dark-700/50 overflow-visible"
            >
              <AccordionTrigger
                className="px-card py-layout-xs"
                disabled={!isDataReady}
              >
                <div className="flex items-center gap-3">
                  <Settings className="w-5 h-5" />
                  <span className="font-semibold">
                    Parameters Configuration
                  </span>
                  {completedSections.includes("parameters") && (
                    <CheckCircle className="w-5 h-5 text-green-500 ml-2" />
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="p-card-sm sm:p-card pt-card-sm min-w-0">
                  {lcBridge.value && (
                    <div className="flex justify-end mb-4">
                      <ImportLcSettingsButton
                        onImport={handleLcSettingsImport}
                      />
                    </div>
                  )}
                  <ParameterConfigPanel
                    values={parameters}
                    onChange={handleParameterChange}
                    pinnedStates={pinnedParameters}
                    onPinnedChange={handlePinnedChange}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Feature Engineering Section */}
            <AccordionItem
              value="features"
              className="bg-dark-800/30 rounded-lg border border-dark-700/50 overflow-visible"
            >
              <AccordionTrigger
                className="px-card py-layout-xs"
                disabled={!isDataReady}
              >
                <div className="flex items-center gap-3">
                  <Activity className="w-5 h-5" />
                  <span className="font-semibold">Feature Engineering</span>
                  {completedSections.includes("features") && (
                    <CheckCircle className="w-5 h-5 text-green-500 ml-2" />
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="p-card-sm sm:p-card pt-card-sm min-w-0">
                  <FeatureBuilder
                    features={features}
                    onChange={setFeatures}
                    featureTypes={featureTypes}
                    slotDefaults={slotDefaults}
                    rangeEnabled={featureRangeEnabled}
                    featureParamState={featureParamState}
                    onParamStateChange={setFeatureParamState}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Preview Section */}
            <AccordionItem
              value="preview"
              className="bg-dark-800/30 rounded-lg border border-dark-700/50 overflow-visible"
            >
              <AccordionTrigger
                className="px-card py-layout-xs"
                disabled={!allConfigReady}
              >
                <div className="flex items-center gap-3">
                  <Code className="w-5 h-5" />
                  <span className="font-semibold">Review & Launch</span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="p-card-sm sm:p-card pt-card-sm min-w-0">
                  <StudyConfigSummary
                    studyName={
                      studyName ||
                      `lorentzian_${symbol.toLowerCase()}_${(timeframe ?? "unknown").toLowerCase()}`
                    }
                    nTrials={nTrials}
                    symbol={symbol}
                    timeframe={timeframe ?? ""}
                    parallelCount={parallelCount}
                    minimumTrades={minimumTrades}
                    objectiveFunction={objectiveFunction}
                    features={features}
                    parameters={parameters}
                    pinnedParameters={pinnedParameters}
                    mode="review"
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="fixed bottom-0 left-20 right-0 z-40 flex flex-col gap-3 border-t border-dark-700/60 bg-background/95 p-3 shadow-2xl backdrop-blur sm:flex-row sm:items-center sm:justify-between lg:static lg:border-0 lg:bg-transparent lg:p-0 lg:pt-6 lg:shadow-none lg:backdrop-blur-0"
          >
            <Button
              variant="outline"
              onClick={() => navigate("/dashboard")}
              className="w-full bg-dark-800/50 border-dark-700 hover:bg-dark-700/50 sm:w-auto"
            >
              Cancel
            </Button>

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
              {/* Always show Start Optimization button - disable if form not ready */}
              {activeSection !== "preview" && (
                <Button
                  onClick={handleSubmit}
                  disabled={!allConfigReady || createStudyMutation.isPending}
                  variant="outline"
                  className={cn(
                    "border-brand-purple hover:bg-brand-purple/10",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                    "w-full sm:w-auto sm:min-w-[180px]",
                  )}
                  data-testid="start-optimization-button"
                >
                  {createStudyMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Rocket className="w-4 h-4" />
                  )}
                  <span className="ml-2">
                    {createStudyMutation.isPending
                      ? "Starting..."
                      : "Start Optimization"}
                  </span>
                </Button>
              )}

              {primaryAction && (
                <Button
                  onClick={primaryAction.onClick}
                  disabled={primaryAction.disabled}
                  className={cn(
                    "bg-gradient-to-r from-brand-blue to-brand-blue-dark",
                    "hover:opacity-90 transition-opacity",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                    "w-full sm:w-auto sm:min-w-[200px]",
                  )}
                  data-testid="next-button"
                >
                  {primaryAction.icon}
                  <span className="ml-2">{primaryAction.label}</span>
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Sample Data Selector Dialog */}
      <SampleDataSelector
        open={showSampleDataDialog}
        onOpenChange={setShowSampleDataDialog}
        onSelect={handleSampleDataSelect}
        isLoading={isLoadingSampleData}
      />

      {/* Low Bar Count Warning Dialog */}
      <LowBarCountWarningDialog
        open={showLowBarWarning}
        onOpenChange={setShowLowBarWarning}
        barCount={pendingParsedData?.data.length ?? 0}
        onProceed={handleLowBarWarningProceed}
        onCancel={handleLowBarWarningCancel}
      />
      <DateFormatDialog
        open={showDateFormatPrompt}
        onOpenChange={setShowDateFormatPrompt}
        sampleDates={ambiguousSampleDates}
        onSelect={handleDateFormatSelected}
      />
    </div>
  );
}
